import {isEqual} from "lodash";
import {createSlice, PayloadAction} from "redux-starter-kit";
import {Disposal, DisposalType, Image, Material} from "../../API";
import {isMockActive} from "./utils";
import {intialDisposalMockState} from "./mockState";

export type UncertainInfo = {
    value: string,
    unknown: boolean
};

export type EnhancedDisposal = Omit<Disposal, "brand" | "model" | "referenceNumber"> & {
    brand?: UncertainInfo,
    model?: UncertainInfo,
    referenceNumber?: UncertainInfo
};

export type DisposalState = {
    currentDisposal: EnhancedDisposal;
    disposals: EnhancedDisposal[];
};

const initialDisposalState: DisposalState = {
    currentDisposal: {
        type: DisposalType.JEWELLERY
    },
    disposals: []
};

export const initialUncertainInfo: UncertainInfo = {
    value: "",
    unknown: false
};

const disposalSlice = createSlice({
    slice: "disposal",
    initialState: isMockActive() ? intialDisposalMockState : initialDisposalState,
    reducers: {
        setDisposalType(state, action: PayloadAction<DisposalType>) {
            state.currentDisposal = initialDisposalState.currentDisposal;
            state.currentDisposal.type = action.payload;
        },
        setMaterials(state, action: PayloadAction<Material[]>) {
            state.currentDisposal.materials = action.payload;
        },
        setBrandValue(state, action: PayloadAction<string>) {
            if (!state.currentDisposal.brand) state.currentDisposal.brand = initialUncertainInfo;
            state.currentDisposal.brand.value = action.payload;
        },
        toggleBrandUnknown(state) {
            if (!state.currentDisposal.brand) {
                state.currentDisposal.brand = {value: "", unknown: true};
            }else {
                state.currentDisposal.brand.unknown = !state.currentDisposal.brand.unknown;
            }
        },
        setModelValue(state, action: PayloadAction<string>) {
            if (!state.currentDisposal.model) state.currentDisposal.model = initialUncertainInfo;
            state.currentDisposal.model.value = action.payload;
        },
        toggleModelUnknown(state) {
            if (!state.currentDisposal.model) {
                state.currentDisposal.model = {value: "", unknown: true};
            }else {
                state.currentDisposal.model.unknown = !state.currentDisposal.model.unknown;
            }
        },
        setRefNrValue(state, action: PayloadAction<string>) {
            if (!state.currentDisposal.referenceNumber) state.currentDisposal.referenceNumber = initialUncertainInfo;
            state.currentDisposal.referenceNumber.value = action.payload;
        },
        toggleRefNrUnknown(state) {
            if (!state.currentDisposal.referenceNumber) {
                state.currentDisposal.referenceNumber = {value: "", unknown: true};
            }else {
                state.currentDisposal.referenceNumber.unknown = !state.currentDisposal.referenceNumber.unknown;
            }
        },
        setComments(state, action: PayloadAction<string>) {
            state.currentDisposal.comments = action.payload;
        },
        setImage(state, action: PayloadAction<Image | null>) {
            state.currentDisposal.image = action.payload;
        },
        addCurrentDisposal(state) {
            if (!state.disposals.some(disposal => isEqual(disposal, state.currentDisposal))) {
                state.disposals = [...state.disposals, state.currentDisposal];
            }
        },
        removeCurrentDisposal(state) {
            state.disposals = state.disposals.filter(stateItem => !isEqual(stateItem, state.currentDisposal));
        },
        resetCurrentDisposal(state) {
            state.currentDisposal = initialDisposalState.currentDisposal;
        },
        removeDisposal(state, action: PayloadAction<EnhancedDisposal>) {
            state.disposals = state.disposals.filter(stateItem => !isEqual(stateItem, action.payload));
        },
        loadDisposalToCurrent(state, action: PayloadAction<EnhancedDisposal>) {
            state.disposals = state.disposals.filter(stateItem => !isEqual(stateItem, action.payload));
            state.currentDisposal = action.payload;
        }
    }
});

export const {
    setDisposalType,
    setMaterials,
    setBrandValue,
    setModelValue,
    setRefNrValue,
    toggleModelUnknown,
    toggleBrandUnknown,
    toggleRefNrUnknown,
    setComments,
    setImage,
    loadDisposalToCurrent,
    removeDisposal,
    resetCurrentDisposal,
    addCurrentDisposal,
    removeCurrentDisposal
} = disposalSlice.actions;

export default disposalSlice.reducer;



